import { DAY } from './format';
import type { SiteRef, SupplierRef } from './types';

/**
 * Ecranul „Azi": ce trebuie făcut, nu ce există.
 *
 * Regula de bază: fiecare rând are **o singură acțiune evidentă**. O cerere
 * apare într-o singură grupă de acțiune (decide → întârziate → aproape de
 * termen), ca să nu te uiți de trei ori la aceeași problemă. „Oferte noi" e
 * singura grupă de informare, deci se poate suprapune cu celelalte.
 *
 * Calculul se face **în browser**, nu pe server: „azi" e ziua utilizatorului.
 * Serverul rulează în UTC, iar un termen de azi ar părea depășit după ora 21
 * pentru cineva din România.
 */

/** Cât timp tace un furnizor până îi propunem un follow-up. */
export const SILENT_DAYS = 3;
/** După un follow-up, îl lăsăm în pace atâtea zile. */
export const FOLLOWUP_COOLDOWN = 2;
/** Ofertele primite în ultimele atâtea zile sunt „noi". */
export const FRESH_DAYS = 3;

export interface TodaySend {
  id: string;
  supplier: SupplierRef;
  /** ISO 8601; null cât timp mailul n-a plecat */
  sentAt: string | null;
  followUps: number;
  lastFollowUp: string | null;
  /** când a venit oferta, sau null dacă furnizorul încă tace */
  offerAt: string | null;
  /** ce e de întrebat despre oferta lui (vezi `offerQuestions`); lipsă = nimic */
  questions?: string[];
  /** ISO 8601: când l-ai întrebat ultima dată */
  askedAt?: string | null;
}

/** Cererea deschisă, cu strictul necesar pentru „Azi". */
export interface TodayRfq {
  id: string;
  title: string;
  category: string;
  site: SiteRef;
  /** dată calendaristică, 'AAAA-LL-ZZ' */
  deadline: string;
  createdAt: string;
  sends: TodaySend[];
}

export interface DecideRow {
  rfq: TodayRfq;
  offers: number;
  /** câți furnizori încă nu au răspuns (poate fi 0) */
  waiting: number;
  /** toți au răspuns, deci nu mai aștepți pe nimeni */
  complete: boolean;
}

export interface LateRow {
  rfq: TodayRfq;
  offers: number;
  waiting: number;
  /** de câte zile a trecut termenul */
  days: number;
}

export interface SilentRow {
  rfq: TodayRfq;
  send: TodaySend;
  /** de câte zile tace (de la trimitere sau de la ultimul follow-up) */
  days: number;
}

export interface DueRow {
  rfq: TodayRfq;
  offers: number;
  waiting: number;
  /** 0 = azi, 1 = mâine */
  days: number;
}

export interface AskRow {
  rfq: TodayRfq;
  send: TodaySend;
  /** pe scurt, ce lipsește: „costul transportului” */
  questions: string[];
  /** de câte zile l-ai întrebat; null dacă încă nu */
  askedDays: number | null;
}

export interface FreshRow {
  rfq: TodayRfq;
  send: TodaySend;
  /** de câte zile a venit oferta (0 = azi) */
  days: number;
}

export interface TodayView {
  /** ai destule oferte: alege și închide */
  decide: DecideRow[];
  /** termenul a trecut și încă aștepți */
  late: LateRow[];
  /** furnizori care tac — acțiunea e follow-up */
  silent: SilentRow[];
  /** termenul e azi sau mâine */
  due: DueRow[];
  /** oferta care ar câștiga lasă ceva nespus: întreabă înainte să alegi */
  ask: AskRow[];
  /** ce a venit cât nu te uitai */
  fresh: FreshRow[];
  /** nimic de făcut și nimic nou */
  quiet: boolean;
}

const ms = (t: string): number => new Date(t).getTime();

/** Data calendaristică locală a unui moment, 'AAAA-LL-ZZ'. */
function isoOf(t: number): string {
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Diferența în zile calendaristice între două date 'AAAA-LL-ZZ'. */
function dayDiff(from: string, to: string): number {
  const a = new Date(`${from}T12:00:00`).getTime();
  const b = new Date(`${to}T12:00:00`).getTime();
  return Math.round((b - a) / DAY);
}

/** De câte zile întregi a trecut un moment. */
const ago = (t: string, now: number): number => Math.max(0, Math.floor((now - ms(t)) / DAY));

/**
 * Împarte cererile deschise în grupe de acțiune.
 * `rfqs` conține **doar cereri deschise** — cele închise nu mai cer nimic.
 */
export function todayView(rfqs: TodayRfq[], now: number = Date.now()): TodayView {
  const azi = isoOf(now);
  const decide: DecideRow[] = [];
  const late: LateRow[] = [];
  const silent: SilentRow[] = [];
  const due: DueRow[] = [];
  const ask: AskRow[] = [];
  const fresh: FreshRow[] = [];

  for (const rfq of rfqs) {
    const offers = rfq.sends.filter((s) => s.offerAt).length;
    const waiting = rfq.sends.length - offers;
    const overdue = dayDiff(rfq.deadline, azi); // > 0 → termenul a trecut

    // Întrebarea merge la furnizor, ca follow-up-ul, nu la cerere: se poate
    // suprapune cu „De decis”, unde e chiar ce trebuie aflat înainte de alegere.
    // După ce ai întrebat, îl lași în pace câteva zile.
    for (const send of rfq.sends) {
      if (!send.questions?.length) continue;
      const askedDays = send.askedAt ? ago(send.askedAt, now) : null;
      if (askedDays != null && askedDays < FOLLOWUP_COOLDOWN) continue;
      ask.push({ rfq, send, questions: send.questions, askedDays });
    }

    // Ofertele proaspete sunt informare, deci se adună indiferent de grupă.
    for (const send of rfq.sends) {
      if (!send.offerAt) continue;
      const days = ago(send.offerAt, now);
      if (days <= FRESH_DAYS) fresh.push({ rfq, send, days });
    }

    // Prioritatea: mai întâi ce poți termina, apoi ce e în întârziere.
    if ((waiting === 0 && offers > 0) || (overdue > 0 && offers > 1)) {
      decide.push({ rfq, offers, waiting, complete: waiting === 0 });
      continue;
    }
    if (overdue > 0) {
      late.push({ rfq, offers, waiting, days: overdue });
    } else if (overdue >= -1) {
      // Math.abs, nu -overdue: pentru termenul de azi ar ieși -0.
      due.push({ rfq, offers, waiting, days: Math.abs(overdue) });
    }

    // Tăcerea se numără pe furnizor, nu pe cerere: unul poate fi restant,
    // altul tocmai a răspuns.
    // Un mail netrimis nu tace: nu i-ai scris încă.
    for (const send of rfq.sends) {
      if (send.offerAt || !send.sentAt) continue;
      const since = send.lastFollowUp ?? send.sentAt;
      const days = ago(since, now);
      const prag = send.lastFollowUp ? FOLLOWUP_COOLDOWN : SILENT_DAYS;
      if (days >= prag) silent.push({ rfq, send, days });
    }
  }

  const byDeadline = (a: { rfq: TodayRfq }, b: { rfq: TodayRfq }) =>
    a.rfq.deadline < b.rfq.deadline ? -1 : a.rfq.deadline > b.rfq.deadline ? 1 : 0;

  decide.sort(byDeadline);
  late.sort((a, b) => b.days - a.days);
  silent.sort((a, b) => b.days - a.days);
  due.sort((a, b) => a.days - b.days);
  ask.sort(byDeadline);
  fresh.sort((a, b) => a.days - b.days);

  return {
    decide,
    late,
    silent,
    due,
    ask,
    fresh,
    quiet:
      !decide.length &&
      !late.length &&
      !silent.length &&
      !due.length &&
      !ask.length &&
      !fresh.length,
  };
}

/**
 * Peste atâtea rânduri, o secțiune nu se mai citește de sus în jos, deci se
 * strânge pe șantiere. Sub prag gruparea ar adăuga doar un clic degeaba.
 */
export const GROUP_FROM = 6;

/** Ce au în comun toate rândurile din „Azi": fiecare ține de o cerere. */
interface RfqRow {
  rfq: Pick<TodayRfq, 'site'>;
}

/** Rândurile unui șantier dintr-o secțiune. */
export interface SiteGroup<T> {
  site: SiteRef;
  rows: T[];
}

/**
 * Grupează rândurile pe șantier, fără să le reordoneze.
 *
 * Grupele ies în ordinea primului rând, deci moștenesc sortarea secțiunii: la
 * „Termen depășit", sortat descrescător pe zile, primul șantier e cel care
 * întârzie cel mai mult. `Map` ține minte ordinea în care au fost puse cheile.
 */
export function groupBySite<T extends RfqRow>(rows: T[]): SiteGroup<T>[] {
  const groups = new Map<string, SiteGroup<T>>();
  for (const row of rows) {
    const found = groups.get(row.rfq.site.id);
    if (found) found.rows.push(row);
    else groups.set(row.rfq.site.id, { site: row.rfq.site, rows: [row] });
  }
  return [...groups.values()];
}

/** Un șantier care are ceva în „Azi", cu cât ocupă din ecran. */
export interface TodaySite {
  site: SiteRef;
  /** câte rânduri are șantierul în tot ecranul, informarea inclusă */
  rows: number;
}

/**
 * Șantierele care au ceva în „Azi", pentru filtrul de sus.
 *
 * Numărăm rânduri, nu cereri: atât ocupă șantierul din ecran, deci atât rămâne
 * dacă îl alegi. „Oferte noi" intră la număr — sunt rânduri pe care le vezi,
 * chiar dacă nu cer nimic.
 *
 * Ordinea e după cât ocupă, apoi alfabetic: filtrul e acolo ca să scurteze
 * ecranul, deci primul e cel care îl umple.
 */
export function todaySites(view: TodayView): TodaySite[] {
  const rows: RfqRow[] = [
    ...view.decide,
    ...view.late,
    ...view.silent,
    ...view.due,
    ...view.ask,
    ...view.fresh,
  ];
  const sites = new Map<string, TodaySite>();
  for (const row of rows) {
    const found = sites.get(row.rfq.site.id);
    if (found) found.rows += 1;
    else sites.set(row.rfq.site.id, { site: row.rfq.site, rows: 1 });
  }
  return [...sites.values()].sort(
    (a, b) => b.rows - a.rows || a.site.name.localeCompare(b.site.name, 'ro'),
  );
}

/** Cum stau ofertele pe cererile deschise, pentru cercul de pe panou. */
export interface OfferMix {
  /** oferte primite */
  received: number;
  /** furnizori de la care aștepți, încă în termen */
  waiting: number;
  /** furnizori care n-au răspuns, iar termenul a trecut */
  late: number;
}

/**
 * Câte oferte ai primit din câte ai cerut, pe cererile deschise.
 *
 * Se numără **pe furnizor**, nu pe cerere: o cerere cu patru furnizori din care
 * a răspuns unul nu e „pe jumătate", e un sfert. Termenul depășit se compară cu
 * ziua locală, ca peste tot.
 */
export function offerMix(rfqs: TodayRfq[], now: number = Date.now()): OfferMix {
  const azi = isoOf(now);
  const mix: OfferMix = { received: 0, waiting: 0, late: 0 };
  for (const rfq of rfqs) {
    const trecut = dayDiff(rfq.deadline, azi) > 0;
    for (const send of rfq.sends) {
      if (send.offerAt) mix.received += 1;
      else if (trecut) mix.late += 1;
      else mix.waiting += 1;
    }
  }
  return mix;
}

/** Felul semnalului unui șantier. */
export type SignalKind = 'decide' | 'late' | 'silent' | 'due' | 'fresh';

/**
 * Ordinea e chiar ordinea secțiunilor de pe „Azi": mai întâi ce poți termina,
 * apoi ce a scăpat de termen, apoi ce aștepți. Dacă ar fi alta, același șantier
 * ar părea că cere altceva pe cele două ecrane.
 */
export const SIGNAL_ORDER: readonly SignalKind[] = ['decide', 'late', 'silent', 'due', 'fresh'];

/** Ce cere un șantier azi, într-o singură vorbă. */
export interface SiteSignal {
  kind: SignalKind;
  /** câte rânduri de felul ăsta are șantierul */
  n: number;
}

/**
 * Un singur semnal pentru fiecare șantier, pentru lista de șantiere.
 *
 * Un șantier poate avea și oferte de ales, și furnizori care tac. Arătăm doar
 * primul din ordinea de mai sus: lista de șantiere spune „pe ăsta deschide-l",
 * nu ține locul ecranului „Azi". Șantierele fără nimic azi lipsesc din hartă.
 */
export function siteSignals(view: TodayView): Map<string, SiteSignal> {
  const counts = new Map<string, Record<SignalKind, number>>();
  const add = (rows: RfqRow[], kind: SignalKind): void => {
    for (const row of rows) {
      const id = row.rfq.site.id;
      let c = counts.get(id);
      if (!c) {
        c = { decide: 0, late: 0, silent: 0, due: 0, fresh: 0 };
        counts.set(id, c);
      }
      c[kind] += 1;
    }
  };
  add(view.decide, 'decide');
  add(view.late, 'late');
  add(view.silent, 'silent');
  add(view.due, 'due');
  add(view.fresh, 'fresh');

  const signals = new Map<string, SiteSignal>();
  for (const [id, c] of counts) {
    const kind = SIGNAL_ORDER.find((k) => c[k] > 0);
    if (kind) signals.set(id, { kind, n: c[kind] });
  }
  return signals;
}

/**
 * Șantierele care cer ceva azi, în față; restul își păstrează ordinea primită
 * de la server. Între două șantiere cu același fel de semnal decide numărul.
 */
export function bySignal<T extends { id: string }>(
  sites: T[],
  signals: Map<string, SiteSignal>,
): T[] {
  const rang = (s: T): number => {
    const semnal = signals.get(s.id);
    return semnal ? SIGNAL_ORDER.indexOf(semnal.kind) : SIGNAL_ORDER.length;
  };
  // `sort` e stabil, deci cele fără semnal rămân în ordinea în care au venit.
  return [...sites].sort(
    (a, b) => rang(a) - rang(b) || (signals.get(b.id)?.n ?? 0) - (signals.get(a.id)?.n ?? 0),
  );
}
